import { Link, Navigate, useLocation } from "react-router-dom";
import type { ReactNode } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useT } from "@/contexts/I18nContext";
import type { UserRole } from "@/types";

/**
 * The route guard.
 *
 * Three outcomes, and the difference between them matters:
 *
 *  - **Not signed in** → `/login`, remembering where they were going.
 *  - **Signed in, wrong role** → a 403 page that says so. Not a redirect and
 *    not a blank: a teacher who types `/billing` deserves a sentence, and a
 *    silent bounce to the dashboard reads as a bug.
 *  - **Password not yet changed** → `/password`, from anywhere.
 *
 * This is convenience, not security. The backend re-checks every role on every
 * call and the Firestore rules re-check every read. A user who edits their way
 * past this guard reaches pages that show them nothing.
 */
/** The first path segment, which is also the nav key: /people/new → "people". */
function section(pathname: string): string {
  return pathname.split("/").filter(Boolean)[0] ?? "dashboard";
}

export function ProtectedRoute({
  children,
  roles,
}: {
  children: ReactNode;
  /** Omit to allow any signed-in staff member. */
  roles?: readonly UserRole[];
}) {
  const { status, user } = useAuth();
  const location = useLocation();
  const t = useT();

  if (status === "loading") {
    return (
      <div className="flex min-h-screen items-center justify-center text-ink-muted">
        {t("common.loading")}
      </div>
    );
  }

  if (status === "signedOut" || !user) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  if (user.mustChangePassword && location.pathname !== "/change-password") {
    return <Navigate to="/change-password" replace />;
  }

  if (roles && !roles.includes(user.role)) {
    /*
      Name the page that was refused, and offer the way back.

      Every owner-only route answered with the same untitled paragraph, so a
      teacher who followed a colleague's link could not tell which of five
      pages had turned them away, and the only way out was the browser's back
      button (live run, 2026-09-09).
    */
    return (
      <div className="mx-auto max-w-lg px-6 py-24 text-center">
        <h1 className="page-title">{t("error.forbidden.title")}</h1>
        <p className="mt-3 text-[14px] text-ink-muted">
          {t("error.forbidden.bodyNamed", { page: t(`nav.${section(location.pathname)}`) })}
        </p>
        <Link
          to="/"
          className="mt-6 inline-block text-[14px] font-medium text-brand underline underline-offset-2 hover:text-brand-hover"
        >
          {t("error.forbidden.back")}
        </Link>
      </div>
    );
  }

  return <>{children}</>;
}
